import React, { useState, useEffect } from 'react'
import { Plus as PlusIcon, Edit as PencilIcon, Trash2 as TrashIcon, Eye as EyeIcon, Search } from 'lucide-react'
import { templateAPI, categoryAPI } from '../services/api'
import { OccasionBadge } from '../components/OccasionScheduler'
import TemplateStageManager from '../components/TemplateStageManager'
import { formatOccasionDisplay, isOccasionActive } from '../utils/occasionUtils'

const TemplateManagementPage = () => {
  const [templates, setTemplates] = useState([])
  const [categories, setCategories] = useState([])
  const [subCategories, setSubCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [stageFilter, setStageFilter] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)

  // Enhanced search states
  const [searchFilters, setSearchFilters] = useState({
    template_id: '',
    templateName: '',
    category_id: '',
    subcategory_id: ''
  })
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    sub_category_id: '',
    tags: '',
    thumbnailUrl: '',
    isPremium: false,
    isActive: true,
    // Occasion-related fields
    isOccasionDateApplicable: false,
    occasionType: 'none',
    occasionStartDate: '',
    occasionEndDate: '',
    occasionStartTime: '',
    occasionEndTime: '',
    isRecurringYearly: false,
    weeklyRecurrenceDays: [],
    monthlyRecurrenceDays: [],
    occasionTimezone: 'Asia/Kolkata',
    occasionMetadata: null
  })

  useEffect(() => {
    fetchTemplates()
    fetchCategories()
  }, [])

  // Fetch templates when search filters change
  useEffect(() => {
    fetchTemplates()
  }, [searchFilters])

  // Fetch subcategories when category changes in form
  useEffect(() => {
    if (formData.category_id) {
      fetchSubCategories(formData.category_id)
    } else {
      setSubCategories([])
    }
  }, [formData.category_id])

  // Fetch subcategories when search category changes
  useEffect(() => {
    if (searchFilters.category_id) {
      fetchSubCategories(searchFilters.category_id)
      // Reset subcategory when category changes
      setSearchFilters(prev => ({ ...prev, subcategory_id: '' }))
    } else {
      setSubCategories([])
      setSearchFilters(prev => ({ ...prev, subcategory_id: '' }))
    }
  }, [searchFilters.category_id])

  const fetchTemplates = async () => {
    try {
      // Build search parameters
      const params = { page: 1, limit: 500 } // Increased limit for better search

      // Add search filters
      if (searchFilters.template_id.trim()) {
        params.search = searchFilters.template_id.trim() // Backend will search by ID
      } else if (searchFilters.templateName.trim()) {
        params.search = searchFilters.templateName.trim() // Backend will search by name
      }

      if (searchFilters.category_id) {
        params.category_id = searchFilters.category_id
      }

      if (searchFilters.subcategory_id) {
        params.category_id = searchFilters.subcategory_id // Use subcategory as category_id
      }

      const response = await templateAPI.getAll(params)
      if (response.data.success) {
        // Handle both data structures
        const templatesData = response.data.data?.templates || response.data.data || []
        setTemplates(templatesData)
      }
    } catch (error) {
      console.error('Error fetching templates:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      // Fetch only root categories (parent categories)
      const response = await categoryAPI.getAll({
        type: 'template',
        isActive: true,
        parentId: '' // This filters for root categories only
      })
      if (response.data.success) {
        const categoriesData = response.data.data || []
        // Sort categories by name
        categoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase()
          const nameB = String(b.name || '').toLowerCase()
          return nameA.localeCompare(nameB)
        })
        setCategories(categoriesData)
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  const fetchSubCategories = async (category_id) => {
    try {
      if (!category_id) {
        setSubCategories([])
        return
      }

      const response = await categoryAPI.getAll({
        parentId: category_id,
        type: 'template',
        isActive: true
      })

      if (response.data.success) {
        const subCategoriesData = response.data.data || []
        // Sort subcategories by name
        subCategoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase()
          const nameB = String(b.name || '').toLowerCase()
          return nameA.localeCompare(nameB)
        })
        setSubCategories(subCategoriesData)
      } else {
        setSubCategories([])
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error)
      setSubCategories([])
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Prevent saving public templates
    if (editingTemplate && (editingTemplate.is_public || editingTemplate.is_public)) {
      alert('Public templates cannot be modified.')
      return
    }

    setLoading(true)

    try {
      const templateData = {
        ...formData,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        // Convert date strings to proper format if they exist
        occasionStartDate: formData.occasionStartDate || null,
        occasionEndDate: formData.occasionEndDate || null,
        occasionStartTime: formData.occasionStartTime || null,
        occasionEndTime: formData.occasionEndTime || null,
        // Ensure proper data types
        weeklyRecurrenceDays: Array.isArray(formData.weeklyRecurrenceDays) ? formData.weeklyRecurrenceDays : [],
        monthlyRecurrenceDays: Array.isArray(formData.monthlyRecurrenceDays) ? formData.monthlyRecurrenceDays : [],
        occasionMetadata: formData.occasionMetadata || null
      }

      if (editingTemplate) {
        await templateAPI.update(editingTemplate.id, templateData)
      } else {
        await templateAPI.create(templateData)
      }

      setShowModal(false)
      setEditingTemplate(null)
      setFormData({
        name: '',
        description: '',
        category_id: '',
        sub_category_id: '',
        tags: '',
        thumbnailUrl: '',
        isPremium: false,
        isActive: true,
        // Occasion-related fields
        isOccasionDateApplicable: false,
        occasionType: 'none',
        occasionStartDate: '',
        occasionEndDate: '',
        occasionStartTime: '',
        occasionEndTime: '',
        isRecurringYearly: false,
        weeklyRecurrenceDays: [],
        monthlyRecurrenceDays: [],
        occasionTimezone: 'Asia/Kolkata',
        occasionMetadata: null
      })
      fetchTemplates()
    } catch (error) {
      console.error('Error saving template:', error)
      alert('Error saving template. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (template) => {
    // Prevent editing public templates
    if (template.is_public || template.is_public) {
      alert('Public templates cannot be modified.')
      return
    }

    setEditingTemplate(template)
    setFormData({
      name: template.name,
      description: template.description || '',
      category_id: template.category_id || '',
      sub_category_id: template.subcategory_id || '',
      tags: template.tags ? template.tags.join(', ') : '',
      thumbnailUrl: template.thumbnailUrl || '',
      isPremium: template.isPremium || false,
      isActive: template.isActive !== false,
      // Occasion-related fields
      isOccasionDateApplicable: template.isOccasionDateApplicable || false,
      occasionType: template.occasionType || 'none',
      occasionStartDate: template.occasionStartDate || '',
      occasionEndDate: template.occasionEndDate || '',
      occasionStartTime: template.occasionStartTime || '',
      occasionEndTime: template.occasionEndTime || '',
      isRecurringYearly: template.isRecurringYearly || false,
      weeklyRecurrenceDays: template.weeklyRecurrenceDays || [],
      monthlyRecurrenceDays: template.monthlyRecurrenceDays || [],
      occasionTimezone: template.occasionTimezone || 'Asia/Kolkata',
      occasionMetadata: template.occasionMetadata || null
    })
    setShowModal(true)
  }

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await templateAPI.delete(id)
        fetchTemplates()
      } catch (error) {
        console.error('Error deleting template:', error)
      }
    }
  }

  const openCreateModal = () => {
    setEditingTemplate(null)
    setFormData({
      name: '',
      description: '',
      category_id: '',
      sub_category_id: '',
      tags: '',
      thumbnailUrl: '',
      isPremium: false,
      isActive: true,
      // Occasion-related fields
      isOccasionDateApplicable: false,
      occasionType: 'none',
      occasionStartDate: '',
      occasionEndDate: '',
      occasionStartTime: '',
      occasionEndTime: '',
      isRecurringYearly: false,
      weeklyRecurrenceDays: [],
      monthlyRecurrenceDays: [],
      occasionTimezone: 'Asia/Kolkata',
      occasionMetadata: null
    })
    setShowModal(true)
  }

  // Search handlers
  const handleSearchChange = (field, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value,
      // Clear other search fields when one is used
      ...(field === 'template_id' && value ? { templateName: '' } : {}),
      ...(field === 'templateName' && value ? { template_id: '' } : {})
    }))
  }

  const clearAllFilters = () => {
    setSearchFilters({
      template_id: '',
      templateName: '',
      category_id: '',
      subcategory_id: ''
    })
    setStageFilter('')
  }

  if (loading && templates.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-64 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Template Management</h1>
          <p className="text-gray-600">Manage video templates for the platform</p>
        </div>
        <button
          onClick={openCreateModal}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          Add Template
        </button>
      </div>

      {/* Enhanced Search and Filters */}
      <div className="mb-6 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Search & Filter Templates</h3>
          <button
            onClick={clearAllFilters}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Clear All Filters
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Template ID Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Template ID
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by ID..."
                value={searchFilters.template_id}
                onChange={(e) => handleSearchChange('template_id', e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Template Name Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Template Name
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name..."
                value={searchFilters.templateName}
                onChange={(e) => handleSearchChange('templateName', e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Category Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={searchFilters.category_id}
              onChange={(e) => handleSearchChange('category_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Subcategory Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subcategory
            </label>
            <select
              value={searchFilters.subcategory_id}
              onChange={(e) => handleSearchChange('subcategory_id', e.target.value)}
              disabled={!searchFilters.category_id}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">All Subcategories</option>
              {subCategories.map((subCategory) => (
                <option key={subCategory.id} value={subCategory.id}>
                  {subCategory.name}
                </option>
              ))}
            </select>
          </div>

          {/* Stage Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stage
            </label>
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Stages</option>
              <option value="pending">Pending</option>
              <option value="submitted">Submitted</option>
              <option value="rejected">Rejected</option>
              <option value="approved">Approved</option>
            </select>
          </div>
        </div>

        {/* Search Results Info */}
        <div className="mt-4 text-sm text-gray-600">
          {searchFilters.template_id || searchFilters.templateName || searchFilters.category_id || searchFilters.subcategory_id || stageFilter ? (
            <span>
              Showing {templates.filter(template => !stageFilter || template.stage === stageFilter).length} filtered results
            </span>
          ) : (
            <span>Showing all {templates.length} templates</span>
          )}
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates
          .filter(template => !stageFilter || template.stage === stageFilter)
          .map(template => (
          <div key={template.id} className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="aspect-video bg-gray-200 relative">
              {template.thumbnailUrl ? (
                <img
                  src={template.thumbnailUrl}
                  alt={template.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  No Preview
                </div>
              )}
              <div className="absolute top-2 right-2 flex flex-col gap-2 items-end">
                {template.isPremium && (
                  <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded">
                    Premium
                  </span>
                )}
                <OccasionBadge template={template} size="xs" showNextDate={false} />
              </div>
            </div>
            
            <div className="p-4">
              <h3 className="font-semibold text-gray-900 mb-2">{template.name}</h3>
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                {template.description || 'No description available'}
              </p>

              {/* Occasion Display */}
              {formatOccasionDisplay(template) && (
                <div className={`text-xs mb-3 px-2 py-1 rounded-md ${
                  isOccasionActive(template)
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-blue-50 text-blue-700 border border-blue-200'
                }`}>
                  {formatOccasionDisplay(template)}
                </div>
              )}

              {template.tags && template.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {template.tags.slice(0, 3).map((tag, index) => (
                    <span
                      key={index}
                      className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                  {template.tags.length > 3 && (
                    <span className="text-xs text-gray-500">
                      +{template.tags.length - 3} more
                    </span>
                  )}
                </div>
              )}

              {/* Template Stage Management */}
              <div className="mb-3">
                <TemplateStageManager
                  template={template}
                  onUpdate={(updatedTemplate) => {
                    setTemplates(prev => prev.map(t =>
                      t.id === updatedTemplate.id ? updatedTemplate : t
                    ))
                  }}
                />
              </div>

              <div className="flex justify-between items-center">
                <span className={`text-xs px-2 py-1 rounded ${
                  template.isActive !== false
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {template.isActive !== false ? 'Active' : 'Inactive'}
                </span>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(template)}
                    className={`p-1 transition-colors ${
                      template.is_public || template.is_public
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-400 hover:text-blue-600'
                    }`}
                    title={template.is_public || template.is_public ? "Public templates cannot be edited" : "Edit"}
                    disabled={template.is_public || template.is_public}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(template.id)}
                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    title="Delete"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {templates.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <PlusIcon className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No templates yet</h3>
          <p className="text-gray-500 mb-4">Get started by creating your first template</p>
          <button
            onClick={openCreateModal}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Add Template
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingTemplate ? 'Edit Template' : 'Add New Template'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value, sub_category_id: '' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subcategory
                </label>
                <select
                  value={formData.sub_category_id}
                  onChange={(e) => setFormData({ ...formData, sub_category_id: e.target.value })}
                  disabled={!formData.category_id}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Select a subcategory</option>
                  {subCategories.map(subCategory => (
                    <option key={subCategory.id} value={subCategory.id}>
                      {subCategory.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="business, presentation, corporate"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Thumbnail URL
                </label>
                <input
                  type="url"
                  value={formData.thumbnailUrl}
                  onChange={(e) => setFormData({ ...formData, thumbnailUrl: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isPremium}
                    onChange={(e) => setFormData({ ...formData, isPremium: e.target.checked })}
                    className="mr-2"
                  />
                  Premium Template
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="mr-2"
                  />
                  Active
                </label>
              </div>

              {/* Occasion Configuration Section */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Occasion Configuration</h3>
                
                <div className="space-y-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.isOccasionDateApplicable}
                      onChange={(e) => setFormData({ ...formData, isOccasionDateApplicable: e.target.checked })}
                      className="mr-2"
                    />
                    Enable Occasion-based Scheduling
                  </label>

                  {formData.isOccasionDateApplicable && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Occasion Type
                        </label>
                        <select
                          value={formData.occasionType}
                          onChange={(e) => setFormData({ ...formData, occasionType: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="none">None</option>
                          <option value="specific_date">Specific Date</option>
                          <option value="date_range">Date Range</option>
                          <option value="recurring_yearly">Recurring Yearly</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </div>

                      {(formData.occasionType === 'specific_date' || formData.occasionType === 'date_range' || formData.occasionType === 'recurring_yearly') && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Start Date
                            </label>
                            <input
                              type="date"
                              value={formData.occasionStartDate}
                              onChange={(e) => setFormData({ ...formData, occasionStartDate: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>

                          {(formData.occasionType === 'date_range' || formData.occasionType === 'recurring_yearly') && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                End Date
                              </label>
                              <input
                                type="date"
                                value={formData.occasionEndDate}
                                onChange={(e) => setFormData({ ...formData, occasionEndDate: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          )}

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Start Time
                              </label>
                              <input
                                type="time"
                                value={formData.occasionStartTime}
                                onChange={(e) => setFormData({ ...formData, occasionStartTime: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                End Time
                              </label>
                              <input
                                type="time"
                                value={formData.occasionEndTime}
                                onChange={(e) => setFormData({ ...formData, occasionEndTime: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                        </>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Timezone
                        </label>
                        <select
                          value={formData.occasionTimezone}
                          onChange={(e) => setFormData({ ...formData, occasionTimezone: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                          <option value="UTC">UTC</option>
                          <option value="America/New_York">America/New_York (EST)</option>
                          <option value="Europe/London">Europe/London (GMT)</option>
                          <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || (editingTemplate && (editingTemplate.is_public || editingTemplate.is_public))}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Saving...' : editingTemplate ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default TemplateManagementPage
